import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "focused", "onFocus", "onBlur", "id", "name", "checked", "onChange", "defaultChecked", "onClick", "readOnly", "size", "textChecked", "textUnchecked", "use"];
import styled, { useTheme } from 'foundations-theming/utils';
import { Checkmark } from '../Checkbox/Checkmark';
import { lazyEval } from '../../utils/lazyEval';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { useFocused } from '../../utils/useFocused';
import { memo } from 'react';
import { useChecked } from '../../utils/useChecked/useChecked';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useIsKeyboardMode } from '../../../components/AppProvider/KeyboardModeProvider';
import { getScreenReaderOnlyStyles } from '../../utils/styles/styles';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultProps = {
  defaultChecked: false,
  size: 'default',
  textChecked: () => unescapedText('salesUI.UIToggle.checkedLabel'),
  textUnchecked: () => unescapedText('salesUI.UIToggle.uncheckedLabel'),
  use: 'default'
};
const SIZE_VALUES = {
  default: 40,
  sm: 32,
  xs: 26
};
const Outer = styled.label.withConfig({
  displayName: "Toggle__Outer",
  componentId: "hin0m5-0"
})(({
  isOnDark,
  disabled,
  readOnly,
  theme,
  $cssStyles
}) => {
  return Object.assign({
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
    userSelect: 'none',
    color: isOnDark ? theme.color['fill-accent-neutral-subtle-alt'] : undefined,
    cursor: disabled || readOnly ? 'not-allowed' : 'pointer'
  }, $cssStyles);
});
const Switch = styled.span.withConfig({
  displayName: "Toggle__Switch",
  componentId: "hin0m5-1"
})(({
  checked,
  disabled,
  readOnly,
  isOnDark,
  size,
  focused,
  theme
}) => {
  const switchSize = SIZE_VALUES[size || 'default'];
  const backgroundColor = disabled ? theme.color['fill-surface-raised'] : readOnly ? theme.color['fill-field-disabled'] : checked ? theme.color['icon-interactive-default'] : isOnDark ? theme.color['fill-accent-neutral-default'] : theme.color['fill-field-disabled'];
  const borderColor = disabled || readOnly ? theme.color['border-core-disabled'] : !checked ? isOnDark ? theme.color['fill-accent-neutral-hover'] : theme.color['border-core-default'] : 'initial';
  const boxShadow = `inset 0 0 0 ${theme.borderWidth[100]} ${borderColor}`;
  return Object.assign({
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
    height: `${switchSize}px`,
    width: size !== 'default' ? `${switchSize * 2}px` : 'initial',
    minWidth: size === 'default' ? '80px' : '0',
    backgroundColor,
    boxShadow,
    borderRadius: theme.borderRadius['100']
  }, focused && Object.assign({}, getFocusStyleObject({
    theme,
    unwrapped: true
  }), {
    // improves visibility in the "on" state
    outlineOffset: '-1px'
  }));
});
const Handle = styled.span.withConfig({
  displayName: "Toggle__Handle",
  componentId: "hin0m5-2"
})(({
  checked,
  size,
  disabled,
  readOnly,
  theme,
  focused
}) => {
  const handleSize = SIZE_VALUES[size || 'default'];
  const paddingValue = size === 'xs' ? 5 : 7;
  const boxShadowColor = disabled || readOnly ? theme.color['border-core-disabled'] : checked ? theme.color['border-interactive-pressed'] : theme.color['border-core-default'];
  return Object.assign({
    position: 'absolute',
    top: 0,
    left: checked ? `calc(100% - (${handleSize}px))` : 0,
    willChange: 'left',
    height: `${handleSize}px`,
    width: `${handleSize}px`,
    padding: `${paddingValue}px`,
    overflow: 'hidden',
    backgroundColor: disabled || readOnly ? theme.color['fill-surface-raised'] : theme.color['icon-core-on-fill-default'],
    boxShadow: `inset 0 0 0 1px ${boxShadowColor}`,
    borderRadius: theme.borderRadius['100'],
    transition: 'all 150ms ease-out'
  }, focused && Object.assign({}, getFocusStyleObject({
    theme,
    unwrapped: true
  }), {
    // improves visibility in the "on" state
    outlineOffset: '-1px'
  }));
});
const Text = styled.span.withConfig({
  displayName: "Toggle__Text",
  componentId: "hin0m5-3"
})(({
  size,
  checked,
  visible,
  disabled,
  readOnly,
  isOnDark,
  theme,
  screenReaderStyles
}) => {
  return Object.assign({
    position: 'relative',
    display: 'block',
    top: size === 'default' && !checked ? '-40px' : '0px',
    textAlign: 'center'
  }, theme.typography['heading-50'], {
    lineHeight: '41px',
    // Line height is 18px in Figma, but 41px is needed for UIToggle parity
    paddingLeft: checked ? theme.space['400'] : theme.space['1200'],
    paddingRight: checked ? theme.space['1200'] : theme.space['400'],
    visibility: !visible ? 'hidden' : 'visible',
    color: disabled ? theme.color['text-core-disabled'] : readOnly ? theme.color['text-core-subtle'] : checked ? theme.color['text-core-on-fill-default'] : isOnDark ? theme.color['text-core-on-fill-default'] : theme.color['text-core-default']
  }, size !== 'default' && screenReaderStyles);
});
const CheckmarkComponent = ({
  disabled,
  readOnly
}) => {
  const theme = useTheme();
  return /*#__PURE__*/_jsx(Checkmark, {
    fill: disabled ? theme.color['icon-core-disabled'] : readOnly ? theme.color['icon-core-subtle'] : theme.color['icon-interactive-default'],
    stroke: disabled || readOnly ? theme.color['fill-surface-raised'] : theme.color['icon-core-on-fill-default'],
    strokeWidth: 2
  });
};
const ToggleComponent = props => {
  const {
      disabled,
      id,
      name,
      defaultChecked: __defaultChecked = defaultProps.defaultChecked,
      onClick,
      readOnly,
      size = 'default',
      textChecked = defaultProps.textChecked,
      textUnchecked = defaultProps.textUnchecked,
      use = defaultProps.use
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    onChange,
    checked
  } = useChecked(props);
  const {
    focused,
    onFocus,
    onBlur
  } = useFocused(props);
  const isKeyboardMode = useIsKeyboardMode();
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const $cssStyles = useMapTokenPropsToCSS(tokenProps);
  const disabledOrReadOnly = disabled || readOnly;
  const shorthandSize = size !== 'default' ? toShorthandSize(size) : size;
  const isOnDark = use === 'on-dark';
  const handleChange = evt => {
    if (disabledOrReadOnly || !onChange) {
      evt.preventDefault();
      return;
    }
    onChange(evt);
  };
  const screenReaderOnlyStyles = getScreenReaderOnlyStyles(useTheme());
  return /*#__PURE__*/_jsxs(Outer, Object.assign({}, domProps, {
    $cssStyles: $cssStyles,
    disabled: disabled,
    readOnly: readOnly,
    isOnDark: isOnDark,
    size: shorthandSize,
    onFocus: onFocus,
    onBlur: onBlur,
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: /*#__PURE__*/_jsx("input", {
        checked: checked,
        disabled: disabled,
        id: id,
        name: name,
        onChange: handleChange,
        onClick: onClick,
        readOnly: readOnly,
        type: "checkbox"
      })
    }), /*#__PURE__*/_jsxs(Switch, {
      checked: checked,
      disabled: disabled,
      readOnly: readOnly,
      focused: focused && isKeyboardMode,
      role: "presentation",
      size: shorthandSize,
      isOnDark: isOnDark,
      children: [/*#__PURE__*/_jsx(Text, {
        checked: true,
        disabled: disabled,
        readOnly: readOnly,
        isOnDark: isOnDark,
        size: shorthandSize,
        visible: checked,
        screenReaderStyles: screenReaderOnlyStyles,
        children: lazyEval(textChecked)
      }), /*#__PURE__*/_jsx(Text, {
        checked: false,
        disabled: disabled,
        readOnly: readOnly,
        isOnDark: isOnDark,
        size: shorthandSize,
        visible: !checked,
        screenReaderStyles: screenReaderOnlyStyles,
        children: lazyEval(textUnchecked)
      }), /*#__PURE__*/_jsx(Handle, {
        checked: checked,
        disabled: disabled,
        readOnly: readOnly,
        isOnDark: isOnDark,
        size: shorthandSize,
        focused: focused && isKeyboardMode,
        children: checked && /*#__PURE__*/_jsx(CheckmarkComponent, {
          disabled: disabled,
          readOnly: readOnly
        })
      })]
    })]
  }));
};
export const Toggle = /*#__PURE__*/memo(ToggleComponent);